import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Observable, Subject, takeUntil } from "rxjs";
import {
  IAppPluginWidget,
  LayoutMode,
  LayoutModeService,
  ModelService,
  IPluginPermissions,
  IPublicViewConfiguration,
  IPublicViewWidget,
  IPlugin,
  PluginInjectorService,
  PluginInjectorWidget,
  WidgetPermissions,
  WidgetDataStore,
  WidgetGridLayoutConfig,
} from "core-client/api";
import { SharedModule } from "../../../shared/shared.module";
import { MatDialog } from "@angular/material/dialog";
import { SwiperOptions } from "swiper/types";
import { SwiperDirective } from "../../../shared/swiper.directive";
import { WidgetHeaderComponent } from "../../../shared/widget-header/widget-header.component";
import { WidgetHostRegistry } from "../../../shared/widget-host-registry.service";

@Component({
  selector: "app-public-main",
  templateUrl: "./main.component.html",
  imports: [SharedModule, SwiperDirective, WidgetHeaderComponent],
  providers: [WidgetHostRegistry],
  encapsulation: ViewEncapsulation.None,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  styleUrls: ["./main.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MainComponent implements OnInit, OnDestroy {
  @ViewChildren("widgetItems", { read: ElementRef })
  widgetItems!: QueryList<ElementRef>;
  widgetInFullscreen: boolean = false;
  private readonly ngUnsubscribe$: Subject<void> = new Subject<void>();
  private readonly boundOnWidgetFullscreenChange =
    this.onWidgetFullscreenChange.bind(this);
  @Input() publicViewConfiguration!: Observable<IPublicViewConfiguration>;
  private publicViewWidgets: IPublicViewWidget[] = [];
  private readonly layoutMode = inject(LayoutModeService);
  isMobileView: boolean = this.layoutMode.isMobile;
  private readonly widgetHosts = inject(WidgetHostRegistry);
  private permissions: IPluginPermissions[] = [];
  private widgetHeaderDisplayTimeout: ReturnType<typeof setTimeout> | null =
    null;
  // Held by id, not on the widget: a rebuild replaces those objects.
  private readonly menuOpenWidgetIds = new Set<string>();

  // swiper config
  swiperConfig: SwiperOptions = {
    initialSlide: 0,
    spaceBetween: 10,
    pagination: { clickable: true },
    navigation: false, // Show navigation arrows
    virtual: false,
  };

  constructor(
    public modelService: ModelService,
    public dialog: MatDialog,
    public pluginInjectorService: PluginInjectorService,
    public widgetPermissions: WidgetPermissions,
    public widgetDataStore: WidgetDataStore,
    public widgetGridLayoutConfig: WidgetGridLayoutConfig,
  ) {}

  async ngOnInit(): Promise<void> {
    this.layoutMode.mode$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((mode: LayoutMode) => {
        const mobile = mode === "mobile";
        if (mobile === this.isMobileView) return;
        this.widgetHosts.detachAll();
        this.isMobileView = mobile;
      });

    this.publicViewConfiguration
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(async (configuration: IPublicViewConfiguration) => {
        this.publicViewWidgets = configuration.widgets;
        if (this.publicViewWidgets.length > 0) {
          await this.loadDashboardWidgets();
        }
      });

    document.addEventListener(
      "fullscreenchange",
      this.boundOnWidgetFullscreenChange,
    );
    document.addEventListener(
      "webkitfullscreenchange",
      this.boundOnWidgetFullscreenChange,
    );
    document.addEventListener(
      "mozfullscreenchange",
      this.boundOnWidgetFullscreenChange,
    );
    document.addEventListener(
      "msfullscreenchange",
      this.boundOnWidgetFullscreenChange,
    );
  }

  ngOnDestroy(): void {
    document.removeEventListener(
      "fullscreenchange",
      this.boundOnWidgetFullscreenChange,
    );
    document.removeEventListener(
      "webkitfullscreenchange",
      this.boundOnWidgetFullscreenChange,
    );
    document.removeEventListener(
      "mozfullscreenchange",
      this.boundOnWidgetFullscreenChange,
    );
    document.removeEventListener(
      "msfullscreenchange",
      this.boundOnWidgetFullscreenChange,
    );

    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private onWidgetFullscreenChange() {
    this.widgetInFullscreen = !!document.fullscreenElement;
    if (this.widgetInFullscreen && this.widgetHeaderDisplayTimeout == null) {
      this.widgetHeaderDisplayTimeout = setTimeout(() => {
        for (const widget of this.widgetDataStore.getWidgets()) {
          widget.isHeaderHovered = false;
        }
        this.clearWidgetHeaderDisplayTimeout();
      }, 3000);
    } else if (!this.widgetInFullscreen) {
      this.clearWidgetHeaderDisplayTimeout();
    }
  }

  private async loadDashboardWidgets(): Promise<void> {
    // we currently only support one widget at a time in a public view
    const publicViewWidget = this.publicViewWidgets[0];

    const plugins: IPlugin[] = await this.modelService._plugin.listAll(
      `name=${publicViewWidget.pluginName.toUpperCase()}`,
    );
    if (plugins.length > 0) {
      // fake appPluginWidget (not existing in DB)
      const appPluginWidget: IAppPluginWidget = {
        _id: "-1",
        enabled: true,
        name: !publicViewWidget.widgetType
          ? ""
          : publicViewWidget.widgetType.charAt(0).toUpperCase() +
            publicViewWidget.widgetType.slice(1).toLowerCase(), // e.g. 'Leaderboard'
        plugin_module: publicViewWidget.widgetType.toUpperCase(), // e.g. 'LEADERBOARD'
        layout: {
          position_x: 0,
          position_y: 0,
          columns: 30,
          rows: 30,
          width_px: 0,
          height_px: 0,
        },
        settings: {
          showWidgetTitle: true,
          widgetHeader: {
            alwaysDisplayed: true,
            onHoverDisplayed: false,
            alwaysOff: false,
            transparent: false,
          },
          leaderboardId: publicViewWidget.settings["selected_id"],
        },
        app_plugin: "-1",
      };

      const widget: PluginInjectorWidget.IWidget = {
        _id: appPluginWidget._id,
        name: appPluginWidget.name,
        x: appPluginWidget.layout.position_x,
        y: appPluginWidget.layout.position_y,
        rows: appPluginWidget.layout.rows,
        cols: appPluginWidget.layout.columns,
        height: appPluginWidget.layout.height_px,
        width: appPluginWidget.layout.width_px,
        pluginId: plugins[0]._id,
        pluginModule: String(appPluginWidget.plugin_module),
        appPluginWidget: appPluginWidget,
        isHeaderHovered: false,
        // marks this widget as public (not linked to any appPluginWidget on any dashboard)
        publicView: {
          enabled: true,
          settings: publicViewWidget.settings,
        },
      };

      this.widgetDataStore.setWidgets([widget]);
    }
  }

  private widgetHeaderPermitted(widget: PluginInjectorWidget.IWidget): boolean {
    const widgetPermissions = this.permissions.find(
      (pluginPermissions: IPluginPermissions) =>
        pluginPermissions.plugin_id === widget.pluginId,
    );
    return widgetPermissions
      ? this.widgetPermissions.check(widget, widgetPermissions, "showHeader")
      : true;
  }

  // a hover header stays in the DOM so that showing and hiding can both transition
  renderWidgetHeader(widget: PluginInjectorWidget.IWidget): boolean {
    const header = widget.appPluginWidget.settings.widgetHeader;
    return (
      this.widgetHeaderPermitted(widget) &&
      (header.alwaysDisplayed || header.onHoverDisplayed)
    );
  }

  showWidgetHeader(widget: PluginInjectorWidget.IWidget): boolean {
    const header = widget.appPluginWidget.settings.widgetHeader;
    return (
      this.widgetHeaderPermitted(widget) &&
      (header.alwaysDisplayed ||
        (header.onHoverDisplayed && widget.isHeaderHovered))
    );
  }

  widgetHeaderIsHovered(widget: PluginInjectorWidget.IWidget): boolean {
    return (
      widget.appPluginWidget.settings.widgetHeader.onHoverDisplayed &&
      widget.isHeaderHovered
    );
  }

  // Only an in-flow header needs separating from the content; the hover header overlays it.
  showWidgetDivider(widget: PluginInjectorWidget.IWidget): boolean {
    return (
      this.showWidgetHeader(widget) &&
      widget.appPluginWidget.settings.widgetHeader.alwaysDisplayed
    );
  }

  private clearWidgetHeaderDisplayTimeout(): void {
    if (this.widgetHeaderDisplayTimeout != null) {
      clearTimeout(this.widgetHeaderDisplayTimeout);
      this.widgetHeaderDisplayTimeout = null;
    }
  }

  onWidgetHover(widget: PluginInjectorWidget.IWidget): void {
    widget.isHeaderHovered = true;
    if (
      (this.isMobileView || this.widgetInFullscreen) &&
      this.widgetHeaderDisplayTimeout == null
    ) {
      this.widgetHeaderDisplayTimeout = setTimeout(() => {
        if (this.menuOpenWidgetIds.has(widget._id)) return;
        widget.isHeaderHovered = false;
        this.clearWidgetHeaderDisplayTimeout();
      }, 3000);
    }
  }

  onWidgetHoverCancel(widget: PluginInjectorWidget.IWidget): void {
    // The header carries the open menu, and reaching that menu means leaving the widget.
    if (this.menuOpenWidgetIds.has(widget._id)) return;
    widget.isHeaderHovered = false;
    this.clearWidgetHeaderDisplayTimeout();
  }

  onWidgetMenuOpenChange(
    widget: PluginInjectorWidget.IWidget,
    open: boolean,
  ): void {
    if (open) {
      this.menuOpenWidgetIds.add(widget._id);
      widget.isHeaderHovered = true;
      return;
    }
    this.menuOpenWidgetIds.delete(widget._id);
    widget.isHeaderHovered = false;
    this.clearWidgetHeaderDisplayTimeout();
  }

  onSwiperSlideChange(): void {
    // set the 'isHeaderHovered' flag to false on all widgets, since in mobile mode only one widget can be displayed at the same time and on slide change any hovering is canceled
    for (const widget of this.widgetDataStore.getWidgets()) {
      widget.isHeaderHovered = false;
    }
    this.clearWidgetHeaderDisplayTimeout();
  }

  async toggleFullscreen(widgetIndex: number): Promise<void> {
    if (!document.fullscreenEnabled) {
      console.warn("Fullscreen API is not supported in this browser.");
      return;
    }

    // Get the widget at this index
    const widgets = this.widgetDataStore.getWidgets();
    const widget = widgets[widgetIndex];

    // Check if a plugin has registered a fullscreen interceptor for this widget
    if (widget) {
      const interceptor = this.pluginInjectorService.tryPluginFullscreen(
        widget.pluginModule,
        "addboard",
      );
      if (interceptor) {
        try {
          const handled = await interceptor.toPromise();
          if (handled) {
            console.log(
              "[PublicDashboard] Video fullscreen toggled by phone widget",
            );
            return;
          }
        } catch (error) {
          console.warn(
            "[PublicDashboard] Phone widget did not handle video fullscreen, falling back to widget fullscreen",
          );
        }
      }
    }

    // Fallback: Do normal widget fullscreen
    const element = this.widgetItems.toArray()[widgetIndex].nativeElement;
    if (!document.fullscreenElement) {
      if (element.requestFullscreen) {
        element.requestFullscreen().catch((error: any) => {
          console.error(
            `Error attempting to enable widget full-screen mode: ${error.message} (${error.name})`,
          );
        });
        /* Firefox */
      } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen().catch((error: any) => {
          console.error(
            `Error attempting to enable widget full-screen mode for Firefox: ${error.message} (${error.name})`,
          );
        });
        /* Chrome, Safari and Opera */
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen().catch((error: any) => {
          console.error(
            `Error attempting to enable widget full-screen mode for WebKit: ${error.message} (${error.name})`,
          );
        });
        /* IE/Edge */
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen().catch((error: any) => {
          console.error(
            `Error attempting to enable widget full-screen mode for IE/Edge: ${error.message} (${error.name})`,
          );
        });
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch((error: any) => {
          console.error(
            `Error attempting to exit widget full-screen mode: ${error.message} (${error.name})`,
          );
        });
        /* Firefox */
      } else if ((document as any).mozCancelFullScreen) {
        (document as any).mozCancelFullScreen().catch((error: any) => {
          console.error(
            `Error attempting to exit widget full-screen mode for Firefox: ${error.message} (${error.name})`,
          );
        });
        /* Chrome, Safari and Opera */
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen().catch((error: any) => {
          console.error(
            `Error attempting to exit widget full-screen mode for WebKit: ${error.message} (${error.name})`,
          );
        });
        /* IE/Edge */
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen().catch((error: any) => {
          console.error(
            `Error attempting to exit widget full-screen mode for IE/Edge: ${error.message} (${error.name})`,
          );
        });
      }
    }
  }
}
